"use client";

import type { MotionValue } from "framer-motion";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";

// Words that use Lora italic + brand gradient on the live Webflow site
const GRADIENT_WORDS = new Set([
  "all",
  "life-changing",
  "resources",
  "support",
  "possible",
  "achievable",
]);

// Pre-split so the render is stable (no split in JSX)
const VISION_WORDS = [
  "We",
  "see",
  "a",
  "world",
  "where",
  "all",
  "individuals,",
  "families,",
  "and",
  "communities",
  "are",
  "able",
  "to",
  "easily",
  "access",
  "life-changing",
  "resources",
  "and",
  "support",
  "\u2014",
  "making",
  "recovery",
  "possible",
  "and",
  "achievable",
  "for",
  "everyone.",
];

function Word({
  word,
  progress,
  range,
}: {
  word: string;
  progress: MotionValue<number>;
  range: [number, number];
}) {
  const opacity = useTransform(progress, range, [0.12, 1]);
  // Grayscale overlay fades out as the word fades in — compositor-only, no paint cost.
  const grayscaleOpacity = useTransform(progress, range, [1, 0]);
  const clean = word.replace(/[^a-z-]/gi, "").toLowerCase();
  const isGradient = GRADIENT_WORDS.has(clean);

  if (isGradient) {
    return (
      <motion.span
        className="relative mr-[0.3em] inline-block font-serif italic"
        style={{ opacity }}
      >
        <span className="text-gradient-brand">{word}</span>
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 text-gradient-brand grayscale"
          style={{ opacity: grayscaleOpacity }}
        >
          {word}
        </motion.span>
      </motion.span>
    );
  }

  return (
    <motion.span
      className="mr-[0.3em] inline-block opacity-[0.12]"
      style={{ opacity }}
    >
      {word}
    </motion.span>
  );
}

// Pre-keyed entries to avoid array-index key in JSX map
const VISION_ENTRIES = VISION_WORDS.map((word, i) => ({
  word,
  id: `word-${i}`,
  n: VISION_WORDS.length,
  i,
}));

function computeProgress(
  scrollPos: number,
  top: number,
  height: number,
  vh: number
): number {
  if (height === 0) {
    return 0;
  }
  const start = top - vh;
  const end = top + height * 0.5 - vh * 0.5;
  return Math.max(0, Math.min(1, (scrollPos - start) / (end - start)));
}

export function VisionSection() {
  const ref = useRef<HTMLElement>(null);
  const { scrollY } = useScroll();

  const sectionTopRef = useRef(0);
  const sectionHeightRef = useRef(0);

  // Raw progress updated imperatively so it's seeded on mount (no lazy-eval jump)
  const rawProgress = useMotionValue(0);
  // Spring smooths any remaining snap on mount and during scroll
  const smoothProgress = useSpring(rawProgress, {
    stiffness: 60,
    damping: 20,
    mass: 0.8,
  });

  useEffect(() => {
    function measure() {
      if (!ref.current) {
        return;
      }
      const rect = ref.current.getBoundingClientRect();
      sectionTopRef.current = rect.top + window.scrollY;
      sectionHeightRef.current = rect.height;
    }

    measure();
    window.addEventListener("resize", measure);

    // Keep rawProgress in sync with scroll
    const unsubscribe = scrollY.on("change", (latest) => {
      rawProgress.set(
        computeProgress(
          latest,
          sectionTopRef.current,
          sectionHeightRef.current,
          window.innerHeight
        )
      );
    });

    return () => {
      window.removeEventListener("resize", measure);
      unsubscribe();
    };
  }, [scrollY, rawProgress]);

  return (
    <section className="py-32" ref={ref}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="heading-2 font-bold leading-relaxed">
            {VISION_ENTRIES.map(({ word, id, n, i }) => (
              <Word
                key={id}
                progress={smoothProgress}
                range={[i / n, Math.min((i + 1.2) / n, 1)]}
                word={word}
              />
            ))}
          </h2>
        </div>
      </div>
    </section>
  );
}
